/**
 * Safe form delivery for the static export.
 *
 * The DataCode-era forms POSTed to `NEXT_PUBLIC_*_FUNCTION_URL` with a
 * `/api/*` fallback. On this fork that is two failure modes at once: with the
 * inherited CI secrets set it silently delivers a DilaCode enquiry into
 * DataCode's production Cloud Functions and inbox; without them it silently
 * 404s, because `output: 'export'` emits no API routes.
 *
 * So corporate forms deliver by `mailto:` — which always works, needs no
 * backend, and cannot misroute — unless an endpoint is *explicitly* configured
 * for this deployment under a `NEXT_PUBLIC_DILACODE_*` name. The old variable
 * names are deliberately not read here, so a stale inherited secret cannot
 * re-point anything.
 *
 * App-compliance forms (account deletion, Zubene support) are NOT routed
 * through this module — they keep their existing integrations, because
 * shipped apps depend on them. See docs/DILACODE_REBRAND_INVENTORY.md.
 */
import { company } from './company';

export type FormChannel = 'mailto' | 'endpoint';

/** Result of a submit attempt, so the UI can say what actually happened. */
export interface SubmitResult {
  ok: boolean;
  channel: FormChannel;
}

function encodeMailto(subject: string, body: string): string {
  return `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Render a plain-text body from ordered field pairs. Kept simple on purpose:
 * `mailto:` bodies have to survive every mail client, so no markup, no tabs.
 */
export function composeBody(fields: Array<[string, string | undefined]>): string {
  return fields
    .filter((pair): pair is [string, string] => Boolean(pair[1] && pair[1].trim()))
    .map(([label, value]) => `${label}:\n${value.trim()}`)
    .join('\n\n');
}

/**
 * Deliver a form.
 *
 * @param endpoint  An explicitly configured URL for this deployment, or
 *                  undefined/empty to use `mailto:`.
 * @param subject   Mail subject when falling back to `mailto:`.
 * @param fields    Ordered label/value pairs for the mail body.
 * @param payload   JSON body when an endpoint IS configured.
 */
export async function deliverForm({
  endpoint,
  subject,
  fields,
  payload,
}: {
  endpoint?: string;
  subject: string;
  fields: Array<[string, string | undefined]>;
  payload: Record<string, unknown>;
}): Promise<SubmitResult> {
  const configured = endpoint?.trim();

  if (configured) {
    const response = await fetch(configured, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return { ok: response.ok, channel: 'endpoint' };
  }

  // No endpoint configured: hand the message to the user's mail client.
  // This is a navigation, not a network request — nothing is transmitted to
  // any third party, and the user sees exactly what is being sent.
  const href = encodeMailto(subject, composeBody(fields));
  if (typeof window !== 'undefined') {
    window.location.href = href;
  }
  return { ok: true, channel: 'mailto' };
}

/** Endpoints, read only from DilaCode-scoped variables. */
export const formEndpoints = {
  contact: process.env.NEXT_PUBLIC_DILACODE_CONTACT_ENDPOINT,
  apply: process.env.NEXT_PUBLIC_DILACODE_APPLY_ENDPOINT,
  support: process.env.NEXT_PUBLIC_DILACODE_SUPPORT_ENDPOINT,
} as const;
